/**
 * Cross-tab communication via BroadcastChannel (T2 + T6).
 *
 * Provides typed messaging between browser tabs for:
 * - Commit notifications (T2: prevents dual-commit conflicts)
 * - Identity state sync (T6: logout/lock propagation)
 * - Registry changes (replaces T4 localStorage listener)
 *
 * Gracefully no-ops when BroadcastChannel is unavailable (SSR, Node tests).
 */

import { sdkConsole } from './logger'

export type CrossTabMessage =
    | { type: 'commit'; blobId: string; head: string }
    | { type: 'sync-complete'; blobId: string; head: string }
    | { type: 'identity-changed' }
    | { type: 'registry-changed' }

export type CrossTabHandler = (msg: CrossTabMessage) => void

const CHANNEL_NAME = 'secureblob-cross-tab'

type NodeAwareBroadcastChannel = BroadcastChannel & {
    unref?: () => void
}

export class CrossTabChannel {
    private channel: NodeAwareBroadcastChannel | null = null
    private handlers = new Set<CrossTabHandler>()

    constructor() {
        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel(CHANNEL_NAME) as NodeAwareBroadcastChannel
            // Node exposes BroadcastChannel too. It must not keep CLI/debug
            // processes alive after the workspace has been closed.
            this.channel.unref?.()
            this.channel.onmessage = (event: MessageEvent<CrossTabMessage>) => {
                for (const handler of this.handlers) {
                    try {
                        handler(event.data)
                    } catch (err) {
                        sdkConsole.warn('[CrossTabChannel] handler error:', err)
                    }
                }
            }
        }
    }

    /** Broadcast a message to all other tabs. */
    broadcast(msg: CrossTabMessage): void {
        this.channel?.postMessage(msg)
    }

    /** Subscribe to messages from other tabs. Returns unsubscribe function. */
    onMessage(handler: CrossTabHandler): () => void {
        this.handlers.add(handler)
        return () => this.handlers.delete(handler)
    }

    /** Close the channel (cleanup on unmount/close). */
    close(): void {
        this.channel?.close()
        this.channel = null
        this.handlers.clear()
        if (_instance === this) _instance = null
    }
}

/** Singleton instance shared across the engine. */
let _instance: CrossTabChannel | null = null

export function getCrossTabChannel(): CrossTabChannel {
    if (!_instance) {
        _instance = new CrossTabChannel()
    }
    return _instance
}
